import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Button,
  Chip,
  Avatar
} from '@mui/material';
import { Link as LinkMUI } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CategoryIcon from '@mui/icons-material/Category';
import { useSelector } from "react-redux";
import { ThemeProvider } from '@mui/material/styles';
import * as Yup from 'yup';
import { useDrawerContext } from '../../../components/DrawerContext/DrawerContext';
import { useConfirmation } from '../../../contexts/ConfirmationContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { getAppTheme } from '../../../services/authContext';
// Import the actual API service
import { blogApi, categoryApi } from '../../../services/mle-api';
import { Link } from 'react-router-dom';

// --- BLOG MANAGEMENT COMPONENT ---
const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const { showDrawerForm } = useDrawerContext();
  const showConfirmation = useConfirmation();
  const { showSnackbar } = useSnackbar();
  const mode = useSelector((state) => state.theme.mode);
  const theme = getAppTheme(mode);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogApi.list();
      setBlogs(response.data.results || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      showSnackbar('Failed to fetch blogs', { severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoryApi.list();
      setCategories(response.data.results || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [fetchBlogs, fetchCategories]);

  const handleBlogSubmit = async (formData, formMode) => {
    // CORRECTED: Use FormData to handle file uploads correctly.
    const payload = new FormData();

    // Append all fields to FormData, handling objects and files correctly
    Object.keys(formData).forEach(key => {
      const value = formData[key];
      console.log(key,value)
      if (key === 'category' && value) {
        payload.append(key, value.id); // Append category ID
      } else if (key === 'featured_image' && value instanceof File) {
        payload.append(key, value); // Append the file object ONLY if it's a new file
      } else if (value !== null && value !== undefined && key !== 'featured_image') {
        // Append other fields, but skip featured_image if it's not a new file
        payload.append(key, value);
      }
    });

    try {
      if (formMode === 'create') {
        await blogApi.create(payload);
        showSnackbar('Blog post created successfully', { severity: 'success' });
      } else {
        await blogApi.update(formData.id, payload);
        showSnackbar('Blog post updated successfully', { severity: 'success' });
      }
      fetchBlogs();
      return true;
    } catch (error) {
      console.error("Submission Error:", error.response?.data || error.message);
      showSnackbar(`Error: ${error.message || 'An unknown error occurred'}`, { severity: 'error' });
      return Promise.reject(error);
    }
  };

  const handleOpenBlogForm = (mode, blogData = {}) => {
    const formFields = [
        { name: 'title', label: 'Blog Title', type: 'text', required: true },
        { 
            name: 'featured_image', 
            label: 'Featured Image (Max 2MB)', 
            type: 'file', 
            accept: 'image/*',
            currentFile: blogData.featured_image || null 
        },
        { 
            name: 'category', 
            label: 'Category', 
            type: 'autocomplete', 
            options: categories, 
            getOptionLabel: (option) => option?.name || "",
            isOptionEqualToValue: (option, value) => option?.id === value?.id,
            required: true 
        },
        { name: 'content', label: 'Content', type: 'textarea', multiline: true, rows: 10, required: true },
        { 
            name: "status",
            label: "Status",
            type: "select",
            options: [
                { value: "PB", label: "Published" },
                { value: "DF", label: "Draft" },
            ],
            required: true
        },
        { name: 'featured', label: 'Mark as Featured Post', type: 'checkbox' }
    ];

    const validationSchema = Yup.object({
        title: Yup.string().required('Title is required'),
        category: Yup.object().nullable().required('Category is required'),
        content: Yup.string().required('Content is required'),
        featured_image: Yup.mixed().nullable()
            .test('fileSize', 'File is too large, max 2MB is allowed.', value => !value || (value && value.size <= 2 * 1024 * 1024))
            .test('fileType', 'Unsupported file format. Please use JPG, PNG, or GIF.', value => !value || (value && ['image/jpeg', 'image/png', 'image/gif'].includes(value.type)))
    });

    let initialValues;
    if (mode === 'create') {
        initialValues = { title: '', category: null, content: '', status: 'DF', featured: false, featured_image: null };
    } else {
        const selectedCategory = categories.find(c => c.id === blogData.category) || null;
        initialValues = { ...blogData, category: selectedCategory, featured_image: null };
    }

    showDrawerForm({
        title: mode === 'create' ? 'Create New Blog Post' : 'Edit Blog Post',
        formFields,
        initialValues,
        validationSchema,
        onSubmit: (values) => handleBlogSubmit(values, mode),
    });
  };

  const handleDeleteBlog = async (blog) => {
    const confirmed = await showConfirmation({
      title: 'Delete Blog Post',
      message: `Are you sure you want to delete "${blog.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmButtonColor: 'error',
    });

    if (confirmed) {
      try {
        await blogApi.delete(blog.id);
        showSnackbar('Blog post deleted successfully', { severity: 'success' });
        fetchBlogs();
      } catch (error) {
        showSnackbar('Failed to delete blog post', { severity: 'error' });
      }
    }
  };

  const filteredBlogs = blogs.filter(blog =>
    blog.title.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Blog Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create, edit, and manage all your blog posts and categories.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              component={Link}
              to="/admin/category-management"
            >
              Manage Categories
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenBlogForm('create')}
            >
              Create Post
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
          <TextField
            placeholder="Search by title..."
            variant="outlined"
            size="small"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            sx={{ width: { xs: '100%', sm: '350px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: 'secondary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white' }}>Image</TableCell>
                <TableCell sx={{ color: 'white' }}>Title</TableCell>
                <TableCell sx={{ color: 'white' }}>Category</TableCell>
                <TableCell sx={{ color: 'white' }}>Date</TableCell>
                <TableCell sx={{ color: 'white' }}>Status</TableCell>
                <TableCell sx={{ color: 'white' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center">Loading...</TableCell></TableRow>
              ) : filteredBlogs.length > 0 ? (
                filteredBlogs.map((blog) => (
                  <TableRow key={blog.id} hover>
                    <TableCell>
                      <Avatar variant="rounded" src={blog.featured_image} alt={blog.title} />
                    </TableCell>
                    <TableCell>
                      <LinkMUI component="button" variant="body2" onClick={() => handleOpenBlogForm('update', blog)}>
                        {blog.title}
                      </LinkMUI>
                    </TableCell>
                    <TableCell>{blog.category_name}</TableCell>
                    <TableCell>{new Date(blog.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip label={blog.status === 'PB' ? 'Published' : 'Draft'} color={blog.status === 'PB' ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => handleOpenBlogForm('update', blog)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDeleteBlog(blog)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">No blog posts found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </ThemeProvider>
  );
};

export default BlogManagement;
